// src/pages/ServicePage/ChatPage.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/DBContext';
import { useLocation } from 'react-router-dom';
import {
  Layout,
  Spin,
  Input,
  Button,
  List,
  Avatar,
  Typography,
  message as antdMessage,
  Grid,
} from 'antd';
import {
  LoadingOutlined,
  SearchOutlined,
  SendOutlined,
  UserOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import './chatapp.css'; // Assuming you have some base styles

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const ChatPage = () => { 
  const { currentUser, fetchConversations, fetchMessages, sendMessage } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [otherUserName, setOtherUserName] = useState('');
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const recipientId = location.state?.recipientId;
  const [showSider, setShowSider] = useState(true);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Fetch conversations on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (currentUser) {
        try {
          const data = await fetchConversations(currentUser.uid);
          setConversations(data);
          setFilteredConversations(data);

          // Check if recipientId is passed from navigation state
          if (recipientId) {
            const selectedConversation = data.find(c => c.participantIds.includes(recipientId));
            if (selectedConversation) {
              handleSelectChat(selectedConversation);
            } else {
              antdMessage.info("No existing conversation with this recipient.");
            }
          } else if (data.length > 0) {
            handleSelectChat(data[0]);
          }
        } catch (error) {
          // console.error("Error fetching conversations:", error);
          antdMessage.error("Failed to load conversations.");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [currentUser, fetchConversations, recipientId]);

  // Fetch messages when currentChat changes
  useEffect(() => {
    if (currentChat && currentUser) {
      setLoading(true);
      const otherUserId = currentChat.participantIds.find(id => id !== currentUser.uid);
      if (otherUserId) {
        const unsubscribe = fetchMessages(currentUser.uid, otherUserId)(newMessages => {
          setMessages(newMessages);
          setLoading(false);
        });

        return () => {
          unsubscribe(); // Cleanup on unmount or chat change
        };
      } else {
        // console.log("Other user ID not found.");
        setLoading(false);
      }
    }
  }, [currentChat, currentUser, fetchMessages]);

  // Toggle sider visibility for mobile
  const toggleSider = () => {
    setShowSider(!showSider);
  };

  // Modify handleSelectChat to handle mobile view
  const handleSelectChat = (conversation) => {
    setCurrentChat(conversation);
    const otherUserId = conversation.participantIds.find(id => id !== currentUser.uid);
    const otherName = conversation.participantNames.find((name, index) => conversation.participantIds[index] === otherUserId);
    setOtherUserName(otherName);

    // Hide sider on mobile after selecting a chat
    if (isMobile) {
      setShowSider(false);
    }

    fetchMessages(currentUser.uid, otherUserId)(newMessages => {
      setMessages(newMessages);
    });
  };

  // Handle sending a message
  const handleMessageSend = () => {
    if (message.trim() && currentChat) {
      const recipientId = currentChat.participantIds.find(id => id !== currentUser.uid);
      const recipientName = currentChat.participantNames.find(name => name !== currentUser.displayName);

      sendMessage(currentUser.uid, recipientName, recipientId, message)
        .then(() => {
          setMessage('');
        })
        .catch(err => {
          // console.error("Failed to send message: ", err);
          antdMessage.error("Failed to send message.");
        });
    }
  };

  // Handle search input change
  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    if (!searchTerm) {
      setFilteredConversations(conversations);
      return;
    }

    const filtered = conversations.filter((conversation) =>
      conversation.participantNames.some(name =>
        name.toLowerCase().includes(searchTerm)
      )
    );

    setFilteredConversations(filtered);
  };

  return (
    <Layout style={{ 
      height: 'calc(100vh - 48px)', // Account for MainLayout padding
      padding: 0,
      overflow: 'hidden',
      background: '#fff'
    }}>
      <Sider
        width={isMobile ? '100%' : 300}
        style={{
          background: '#fff',
          borderRight: '1px solid #f0f0f0',
          overflow: 'hidden',
          height: '100%',
          position: 'relative',
          display: (!isMobile || showSider) ? 'block' : 'none'
        }}
      >
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '16px' }}>
          <Title level={4} style={{ marginTop: isMobile ? '24px' : '0', marginBottom: '16px' }}>Chats</Title>
          <Input
            placeholder="Search by name..."
            prefix={<SearchOutlined />}
            allowClear
            onChange={handleSearch}
            style={{ marginBottom: '16px' }}
            size="large"
          />
          <div style={{ 
            flex: 1,
            overflowY: 'auto',
            marginRight: '-16px',
            paddingRight: '16px'
          }}>
            <List
              itemLayout="horizontal"
              dataSource={filteredConversations}
              renderItem={conversation => {
                const otherUserId = conversation.participantIds.find(id => id !== currentUser.uid);
                const otherName = conversation.participantNames.find(name => conversation.participantIds[conversation.participantNames.indexOf(name)] === otherUserId);
                return (
                  <List.Item
                    key={conversation.id}
                    onClick={() => handleSelectChat(conversation)}
                    style={{ 
                      cursor: 'pointer', 
                      background: currentChat?.id === conversation.id ? '#e6f7ff' : 'transparent',
                      padding: '8px 16px',
                    }}
                  >
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} />}
                      title={otherName}
                    />
                  </List.Item>
                );
              }}
            />
          </div>
        </div>
      </Sider>

      <Layout style={{ 
        display: isMobile && showSider ? 'none' : 'flex',
        height: '100%',
        background: '#fff'
      }}>
        <Content style={{ 
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Chat Header */}
          <div style={{ 
            padding: '24px 24px 8px 24px',
            borderBottom: '1px solid #f0f0f0', 
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {isMobile && currentChat && (
              <Button 
                type="text" 
                icon={<ArrowLeftOutlined />} 
                onClick={() => setShowSider(true)}
                style={{ padding: 0 }}
              />
            )}
            <div>
              <Title level={4} style={{ margin: 0 }}>{currentChat ? otherUserName : 'Select a chat...'}</Title>
              {currentChat && <Text type="secondary">{messages.length} messages</Text>}
            </div>
          </div>

          {/* Chat History */}
          <div style={{ 
            flex: 1, 
            overflowY: 'auto',
            padding: '0 24px'
          }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
              </div>
            ) : (
              <List
                dataSource={messages}
                renderItem={msg => (
                  <List.Item
                    key={msg.id}
                    style={{
                      display: 'flex',
                      justifyContent: msg.senderId === currentUser.uid ? 'flex-end' : 'flex-start',
                      padding: '8px 0',
                      border: 'none'
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '60%',
                        background: msg.senderId === currentUser.uid ? '#1890ff' : '#f0f0f0',
                        padding: '8px 16px',
                        borderRadius: '16px',
                        wordBreak: 'break-word',
                        color: msg.senderId === currentUser.uid ? '#ffffff' : '#000000',
                      }}
                    >
                      <span style={{ color: msg.senderId === currentUser.uid ? '#ffffff' : '#000000' }}>
                        {msg.message}
                      </span>
                      <div style={{ fontSize: '12px', marginTop: '4px', textAlign: msg.senderId === currentUser.uid ? 'right' : 'left', color: msg.senderId === currentUser.uid ? '#ffffff' : '#000000' }}>
                        {new Date(msg.timestamp.seconds * 1000).toLocaleTimeString()}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            )}
          </div>

          {/* Message Input */}
          <div style={{ 
            padding: '16px 24px',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Input
              placeholder="Enter your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onPressEnter={handleMessageSend}
              style={{ marginRight: '8px', flex: 1 }}
              size="large"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleMessageSend}
              disabled={!message.trim()}
              size="large"
            >
              Send
            </Button>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default ChatPage;
